import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { entries, omit, orderBy, values } from 'lodash';
import {
  Box,
  SimpleGrid,
  Text,
  GridItem,
  Flex,
  Tooltip,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Radio,
  Badge,
  Card,
} from '@chakra-ui/react';
import { Moment } from 'moment';
import { getCalendarSlot, momentTimezone } from '../utils/dateUtils';
import { allIcons, CirlceIcon } from '../utils/Icon';
import {
  addressFormat,
  getContrastedColor,
  getReadableColor,
  isEmpty,
  mergeWithArray,
  regCompare,
} from '../../helpers';
import { updateAppointment } from '../../api/planningApi';
import { generateGoogleMapsURL } from './MapUrl';
import { TState, useActions } from '../../redux/store';
import { useCanI, useGetSelect } from '../../redux/actions';
import Loader from '../common/Loader';
import DraggableItem from '../dragAndDrop/DraggableItem';
import DropMinutes from './DropMinutes';
import useForm from '../form/Form';
import DynamicFilters from '../table/tableComponents/DynamicFilters';
import { TColumnData } from '../tabs/type';
import useFilters from '../table/tableComponents/useFilters';
import DistanceTag from './DistanceTag';
import { TCalendarColumnProps } from './types';
import HourCheck from './HourCheck';
import CalendarToolbar from './CalendarToolbar';
import MapCluster from './MapCluster';
import { calendarHours } from './calendarHours';

const daysName = [
  'Dimanche',
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
];

const CalendarColumnHeader = ({
  head,
  isCurrent,
}: {
  head: string;
  isCurrent: boolean;
}) => {
  const theme = useSelector((state: TState) => state.theme);
  const isMobile = useSelector((state: TState) => state.isMobile);
  const isTablet = useSelector((state: TState) => state.isTablet);

  const isPC = !isMobile && !isTablet;

  return (
    <GridItem>
      <Box
        p={1}
        bgGradient={isPC ? `linear(to-r, ${theme.normal}, ${theme.dark})` : ''}
        bgColor={!isPC && isCurrent ? theme.none : ''}
        borderWidth={!isPC && isCurrent ? '1px' : ''}
        color={isPC ? theme.none : theme.main}
        borderRadius='5px'
        borderBottomRadius={isPC ? 0 : ''}
        textAlign='center'
        mx={isPC ? -2 : 0}
        mt={isPC ? '' : 3}
        mb={isPC ? 3 : ''}
      >
        <Text
          fontWeight='bold'
          fontSize={[theme.fontSize.xxSmall, theme.fontSize.small]}
        >
          {head}
        </Text>
      </Box>
    </GridItem>
  );
};

const CalendarColumn = ({
  appointments,
  day,
  onCheck,
  selected,
  forceDistance,
  formData,
  setCurrDay,
  isPC,
}: // selectedUsers,
TCalendarColumnProps) => {
  const rowData = useSelector((state: TState) => state.rowData);
  const user = useSelector((state: TState) => state.user);
  const modalType = useSelector((state: TState) => state.modalType);
  const isPlanningEdit = useSelector((state: TState) => state.isPlanningEdit);
  const globalFilter = useSelector((state: TState) => state.globalFilter);
  const theme = useSelector((state: TState) => state.theme);
  const calendarCurrentDay = useSelector(
    (state: TState) => state.calendarCurrentDay,
  );
  const planningTypesColors = useSelector(
    (state: TState) =>
      state.dataInputs.filter((input) => input.name_id === 'planning_type')[0]
        ?.colors,
  );

  const currDay = momentTimezone(calendarCurrentDay);

  const {
    setModalData,
    setRowData,
    setModalType,
    fetchAppointments,
    getDataDetail,
  } = useActions();

  const canI = useCanI();

  const displayAllSlots = onCheck || isPlanningEdit;
  const isUpdate = modalType === 'updatePlanning';
  const noFilter = modalType;

  useEffect(() => {
    isUpdate && onCheck?.(momentTimezone(rowData.planningDate));
  }, []);

  const filterFn = useFilters(true)();
  const nbRdv = values(appointments).flatMap((a) => a).length;

  const canConfirm = canI('planning', 'confirm');
  const canUpdate = canI('planning', 'update');
  const canUpdateData = canI('planning', 'updateData');
  const canDelete = canI('planning', 'delete');

  return (
    <GridItem
      borderRadius='5px'
      px={2}
      bgColor='#fff'
      boxShadow='xl'
      mb={'25px'}
    >
      {isPC ? (
        <Box
          onClick={() =>
            isPC ? null : setCurrDay(momentTimezone(day).toISOString())
          }
          cursor={isPC ? 'default' : 'pointer'}
          position='sticky'
          top={0}
          zIndex={1}
        >
          <CalendarColumnHeader
            key={momentTimezone(day).format('DD/MM/YYYY')}
            head={`${daysName[day.format('d') as any]} ${day.format('D')}${
              nbRdv ? ` (${nbRdv})` : ''
            }`}
            isCurrent={!isPC && day.isSame(currDay, 'day')}
          />
        </Box>
      ) : (
        <Box mb={2} />
      )}
      {calendarHours
        .map((hour) => {
          const appoints = orderBy(
            appointments?.[`${hour}`],
            (appoint) => appoint.planningDate,
          );
          // const appointsBefore = appointments?.[`${hour - 1}`];
          const slotDate = getCalendarSlot(day, hour);
          const isSelected = selected?.isSame(slotDate, 'hour');

          // const isAvailable = (!selectedUsers.length && appoints) ||
          // const isAvailable =
          //   !appoints?.find((appoint) =>
          //     selectedUsers.includes(appoint.assignedUser),
          //   ) &&
          //   !appointsBefore?.find((appoint) =>
          //     selectedUsers.includes(appoint.assignedUser),
          //   );

          // const [dragHoverO, setDragHoverO] = useState(false);

          if (!displayAllSlots && !appoints?.length) return null;

          const authUsers = [user._id, ...(user?.assignedUsers || [])];

          return (
            <Box
              my={displayAllSlots ? 1 : 0}
              p={displayAllSlots ? 1 : 0}
              backgroundColor={displayAllSlots ? theme.lighter : 'none'}
              borderWidth={displayAllSlots ? '1px' : 0}
              borderColor={displayAllSlots ? theme.light : 'none'}
              borderRadius={5}
            >
              {displayAllSlots && (
                <Flex
                  cursor={onCheck ? 'pointer' : ''}
                  onClick={() => onCheck?.(slotDate)}
                  backgroundColor={isSelected ? theme.normal : theme.lighter}
                  borderRadius={5}
                  alignItems='center'
                  px={2}
                  py={0.5}
                >
                  {!isSelected && <Text fontWeight={500}>{`${hour}H`}</Text>}
                  {onCheck && (
                    <Flex flexGrow={1}>
                      {isSelected && (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <HourCheck
                            hour={hour}
                            onCheck={onCheck}
                            selected={selected?.format('mm')}
                          />
                        </Box>
                      )}
                      <Box flexGrow={1} />
                      <Radio isChecked={isSelected} />
                    </Flex>
                  )}
                  {!onCheck && isPlanningEdit && (
                    <DropMinutes day={day} hour={hour} />
                  )}
                </Flex>
              )}
              {appoints?.map((appoint: any) => {
                const formIds = new Set(values(appoint.formData).flat());

                const isOwner =
                  authUsers.some(
                    (id) =>
                      formIds.has(id) ||
                      appoint.createdBy?._id === id ||
                      appoint.assignedUser === id,
                  ) ||
                  user.isAdmin ||
                  user.noRestrict;

                const isSupervisor = user.assignedUsers?.length;
                const displayPanel =
                  user.noRestrictPlanning ||
                  (isOwner && (canConfirm || canUpdate || canUpdateData)) ||
                  canDelete;

                const destinationAddress = `${
                  appoint.planningAddressDescription ||
                  appoint.destinationAddress ||
                  ''
                }`;

                const globalSearchString = `${values(appoint.rowData)
                  .map((rd) => rd.value)
                  .join(' ')} ${destinationAddress} ${
                  appoint.user?.name || ''
                }`;

                const searchData = {
                  ...appoint.formData,
                  planningAddressDescription:
                    appoint.planningAddressDescription,
                };

                const notMatch =
                  entries(
                    omit(formData, [
                      'planning_type',
                      'selectedUsers',
                      'destinationAddress',
                      'confirmed',
                    ]),
                  ).filter(([k, v]) => filterFn(searchData, k, v) === false)
                    .length > 0;

                const matchAddress = regCompare(
                  formData.destinationAddress,
                  destinationAddress,
                );

                const matchConfirmed =
                  formData.confirmed === undefined ||
                  (formData.confirmed === true && appoint.isConfirmed) ||
                  (formData.confirmed === false && !appoint.isConfirmed);

                const matchFilters =
                  noFilter ||
                  (!notMatch &&
                    matchAddress &&
                    matchConfirmed &&
                    regCompare(globalFilter, globalSearchString));

                const bgType = planningTypesColors?.[appoint.planningType];

                return !matchFilters ||
                  (isSupervisor && !displayPanel) ? null : (
                  <Accordion
                    allowToggle
                    mb={1}
                    defaultIndex={onCheck ? [] : 0}
                    boxShadow='xl'
                  >
                    <AccordionItem position='relative' border='none' mb={4}>
                      <Card>
                        <DraggableItem
                          id={appoint._id}
                          noDrag={
                            !isPlanningEdit ||
                            appoint.isConfirmed ||
                            isUpdate ||
                            !isOwner ||
                            !canUpdate
                          }
                          onDrag={() => setRowData(appoint)}
                          onCancel={() => {
                            setRowData({});
                          }}
                          type='CALENDAR'
                        >
                          <AccordionButton
                            px={1}
                            height={6}
                            color={getReadableColor(appoint.user?.userColor)}
                            borderRadius={displayPanel ? '5px 5px 0 0' : '5px'}
                            _hover={{
                              borderColor: appoint.user?.userColor,
                              opacity: 0.8,
                            }}
                            fontSize={theme.fontSize.small}
                            cursor={
                              isPlanningEdit && isOwner && canUpdate
                                ? 'grab'
                                : 'pointer'
                            }
                            bgGradient={`linear(to-r, ${
                              appoint.user?.userColor || 'lightgray'
                            }, ${getContrastedColor(
                              appoint.user?.userColor || '#d3d3d3',
                              40,
                            )})`}
                          >
                            <Flex w='100%' alignItems='center'>
                              <Text fontWeight='bold' mr={2}>
                                {momentTimezone(appoint.planningDate)
                                  .format('HH:mm')
                                  .replace(':', 'h')}
                              </Text>
                              <Text textTransform='capitalize'>
                                {appoint.user?.name || 'Non assigné'}
                              </Text>
                              <Box flexGrow={1} />
                              <DistanceTag
                                appoint={appoint}
                                forceDistance={forceDistance}
                              />
                              <Flex
                                h={6}
                                borderTopRightRadius='5px'
                                borderBottomRightRadius={
                                  displayPanel ? '' : '5px'
                                }
                                bgColor={bgType}
                                bgGradient={`linear(to-r, ${
                                  bgType || 'lightgray'
                                }, ${getContrastedColor(
                                  bgType || '#d3d3d3',
                                  10,
                                )})`}
                                color={getReadableColor(bgType)}
                                mr='-4px'
                                alignItems='center'
                              >
                                <Text px={2}>
                                  {appoint.planningAddress_postal}
                                </Text>
                              </Flex>
                            </Flex>
                          </AccordionButton>
                        </DraggableItem>
                        {displayPanel && (
                          <AccordionPanel
                            bgColor={appoint.isConfirmed ? 'green.100' : ''}
                            cursor={
                              isPlanningEdit && !isOwner
                                ? 'not-allowed'
                                : 'default'
                            }
                            fontSize={theme.fontSize.small}
                            pt={2}
                            mt={-1}
                            pb={1}
                          >
                            <Flex>
                              <Text fontWeight='bold' mr={1}>
                                Adresse
                              </Text>
                              :
                              <Text ml={1}>
                                {addressFormat({
                                  street: appoint.planningAddress_street,
                                  city: appoint.planningAddress_city,
                                  postal: appoint.planningAddress_postal,
                                })}
                              </Text>
                            </Flex>
                            {appoint.rowData.map((data: any) => (
                              <Flex mb={0.5}>
                                <Text
                                  fontWeight='bold'
                                  mr={1}
                                  textTransform='capitalize'
                                >
                                  {data.label}
                                </Text>
                                :
                                {data.color ? (
                                  <Badge
                                    bgColor={data.color}
                                    my='auto'
                                    borderRadius={5}
                                    ml={1}
                                    color={getReadableColor(data.color)}
                                    fontSize={theme.fontSize.small}
                                  >
                                    {data.value}
                                  </Badge>
                                ) : data.colors?.length ? (
                                  data.colors.map((c: string, idx: number) => (
                                    <Badge
                                      bgColor={c}
                                      my='auto'
                                      borderRadius={5}
                                      ml={1}
                                      color={getReadableColor(c)}
                                      fontSize={theme.fontSize.xSmall}
                                    >
                                      {data.value?.[idx]}
                                    </Badge>
                                  ))
                                ) : (
                                  <Text ml={1}>{data.value}</Text>
                                )}
                              </Flex>
                            ))}
                            <Flex py={1}>
                              <Box flexGrow={1} />
                              <Tooltip label='Y aller'>
                                <span>
                                  <CirlceIcon
                                    boxSize='25px'
                                    as={allIcons.HiMapPin}
                                    onClick={() =>
                                      generateGoogleMapsURL(
                                        appoint.planningAddressDescription,
                                      )
                                    }
                                  />
                                </span>
                              </Tooltip>
                              {canConfirm && isOwner && (
                                <Tooltip
                                  label={
                                    appoint.isConfirmed
                                      ? 'Déconfirmer'
                                      : 'Confirmer'
                                  }
                                >
                                  <span>
                                    <CirlceIcon
                                      boxSize='25px'
                                      as={allIcons.HiCheck}
                                      backgroundColor={
                                        appoint.isConfirmed ? 'green.400' : ''
                                      }
                                      color={
                                        appoint.isConfirmed ? theme.dark : ''
                                      }
                                      cursor='pointer'
                                      onClick={async () => {
                                        await updateAppointment(appoint._id, {
                                          isConfirmed: !appoint.isConfirmed,
                                        });
                                        await fetchAppointments();
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                              {canUpdate && isOwner && (
                                <>
                                  <Tooltip label='Modifier RDV'>
                                    <span>
                                      <CirlceIcon
                                        boxSize='25px'
                                        as={allIcons.HiCalendarDays}
                                        onClick={() => {
                                          setRowData(appoint);
                                          setModalType('updatePlanning');
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                              {canUpdateData && isOwner && (
                                <>
                                  <Tooltip label='Modifier dossier'>
                                    <span>
                                      <CirlceIcon
                                        boxSize='25px'
                                        as={allIcons.HiPencilSquare}
                                        onClick={async () => {
                                          await getDataDetail({
                                            rowId: appoint.rowId,
                                          });
                                          setModalData();
                                          setRowData(appoint.formData || {});
                                          setModalType('editEntry');
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                              {canDelete && (
                                <Tooltip label='Supprimer le rdv'>
                                  <span>
                                    <CirlceIcon
                                      boxSize='25px'
                                      as={allIcons.HiMiniXMark}
                                      onClick={() => {
                                        setRowData(appoint);
                                        setModalType('deletePlanning');
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </Flex>
                          </AccordionPanel>
                        )}
                      </Card>
                    </AccordionItem>
                  </Accordion>
                );
              })}
            </Box>
          );
        })}
    </GridItem>
  );
};

const flatAppointments = (data: Record<string, Record<string, any>>) =>
  Object.values(data)
    .flatMap((obj) => Object.values(obj))
    .flat();

const Calendar = ({
  selectedDate,
  setSelectedDate,
  datas,
  children,
}: {
  selectedDate?: Moment;
  setSelectedDate?: (date: Moment) => void;
  datas?: Record<string, any>;
  children?: JSX.Element;
}) => {
  const isMobile = useSelector((state: TState) => state.isMobile);
  const isTablet = useSelector((state: TState) => state.isTablet);
  const appointments = useSelector((state: TState) => state.appointments);
  const isPlanningEdit = useSelector((state: TState) => state.isPlanningEdit);
  const filterActive = useSelector((state: TState) => state.filterActive);
  const modalType = useSelector((state: TState) => state.modalType);
  const planningTab = useSelector((state: TState) => state.planningTab);
  const userList = useSelector((state: TState) => state.userList);
  const pendings = useSelector((state: TState) => state.pendings);

  const calendarCurrentDay = useSelector(
    (state: TState) => state.calendarCurrentDay,
  );

  const {
    getUsersList,
    setCalendarCurrentDay: setCurrDay,
    onClose,
  } = useActions();

  const canI = useCanI();

  const currDay = momentTimezone(calendarCurrentDay);

  const [forceDistance, setForceDistance] = useState(false);
  const [isMap, setIsMap] = useState(false);
  const [displayWeekend, setDisplayWeekend] = useState(
    localStorage.getItem('displayWeekend') === 'true',
  );

  const getInputByName = useGetSelect();
  const planningTypes = getInputByName('planning_type');

  const allUsers = userList
    .filter((u) => u.isPlanable)
    .map((u) => ({
      value: u._id,
      label: u.name,
      color: u.userColor,
    }));

  const notEdit =
    !isPlanningEdit && !/addToPlanning|updatePlanning/i.test(modalType);

  const editFilters = [
    {
      label: 'Assigné à',
      name_id: 'selectedUsers',
      type: 'users',
      options: allUsers,
    },
    planningTypes as TColumnData,
    {
      label: 'Adresse de destination',
      name_id: 'destinationAddress',
      type: 'text',
    },
  ];

  const inputs = (
    notEdit
      ? [
          ...editFilters,
          ...(planningTab?.tableColumns[0]?.tableColumns || []),
          {
            label: 'Confirmé',
            name_id: 'confirmed',
            type: 'tripleChoice',
          },
        ]
      : editFilters
  )
    .flatMap((input) => {
      const type = input?.type;
      if (!type) return {};

      const filterInput: any = /select/.test(type)
        ? { ...input, type: 'multiselect' }
        : /users/.test(type)
          ? { ...input, isMulti: true }
          : /date/.test(type)
            ? { ...input, type: 'date_range' }
            : /address/.test(type)
              ? null
              : /number/.test(type)
                ? { ...input, type: 'number_range' }
                : input;

      return /address/.test(type)
        ? null
        : { ...filterInput, required: false, advised: false };
    })
    .filter((f) => f);

  const props = useForm(inputs, { noRowData: true });

  const isModal = /addToPlanning|updatePlanning/i.test(modalType);
  const isEntryModal = /editEntry/i.test(modalType);

  const isPC = !isMobile && !isTablet;
  const nbCol = isPC ? (displayWeekend ? 7 : 5) : 1;

  const days = isPC
    ? Array(nbCol)
        .fill('')
        .map((d, idx) =>
          momentTimezone(currDay).startOf('isoWeek').add(idx, 'day'),
        )
    : [momentTimezone(currDay)];

  const selectDays = Array(displayWeekend ? 7 : 5)
    .fill('')
    .map((d, idx) =>
      momentTimezone(currDay).startOf('isoWeek').add(idx, 'day'),
    );

  const changeDay = (moins?: boolean) => {
    setCurrDay((c: any) => {
      const nDate = momentTimezone(c);
      nDate[moins ? 'subtract' : 'add'](7, 'day');
      return nDate.toISOString();
    });
  };

  const currAppoints = props.formData.planning_type?.length
    ? props.formData.planning_type.map((type: string) => appointments[type])
    : appointments;

  const formatedAppoints = values(currAppoints).reduce(
    (prev, val) => mergeWithArray(prev, val),
    {},
  );

  const filteredAppoint = !values(formatedAppoints)?.length
    ? []
    : !props.formData.selectedUsers?.length
      ? values(formatedAppoints).reduce(
          (prev, val) => mergeWithArray(prev, val),
          {},
        )
      : props.formData.selectedUsers.reduce(
          (prev: any, user: any) =>
            mergeWithArray(prev, formatedAppoints[user]),
          {},
        );

  const isUAMobile =
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  const displayUpdateRDV =
    !setSelectedDate && canI('planning', 'update') && !isUAMobile;

  const isPending = pendings['global/async/fetchAppointments/pending'] > 0;

  const toolbarProps = {
    currDay,
    displayWeekend,
    displayUpdateRDV,
    forceDistance,
    isMap,
    setIsMap,
    setForceDistance,
    setDisplayWeekend,
    changeDay,
  };

  useEffect(() => {
    if (isEntryModal) {
      onClose();
    } else {
      notEdit && setCurrDay(momentTimezone().toISOString());
    }

    getUsersList();
  }, []);

  useEffect(() => {
    const { planningType: pt, assignedUser: au } = datas || {};
    props.setFormData({
      ['planning_type']: isEmpty(pt) ? null : [pt],
      selectedUsers: isEmpty(au) ? null : [au],
    });
  }, [datas]);

  useEffect(() => {
    !displayUpdateRDV && setForceDistance(false);
  }, [displayUpdateRDV]);

  useEffect(() => {
    localStorage.setItem('displayWeekend', displayWeekend ? 'true' : 'false');
  }, [displayWeekend]);

  return (
    <Flex gap={3} flexDirection='column' h={isPC || !isModal ? '100%' : ''}>
      {isPending && <Loader />}
      <CalendarToolbar {...toolbarProps}>
        {!isPC && (
          <SimpleGrid columns={displayWeekend ? 7 : 5} spacing={1}>
            {selectDays.map((day) => (
              <Box
                onClick={() =>
                  isPC ? null : setCurrDay(momentTimezone(day).toISOString())
                }
                cursor={isPC ? 'default' : 'pointer'}
              >
                <CalendarColumnHeader
                  key={momentTimezone(day).format('DD/MM/YYYY')}
                  head={`${daysName[day.format('d') as any]} ${day.format('D')}`}
                  isCurrent={!isPC && day.isSame(currDay, 'day')}
                />
              </Box>
            ))}
          </SimpleGrid>
        )}
      </CalendarToolbar>
      {children ? (
        <Flex flexDirection={['column', 'column', 'row']} gap={3}>
          <Box flex={children ? [1, 1, 0.6, 0.4] : 1}>
            <DynamicFilters
              {...props}
              inputs={editFilters}
              show={filterActive || isModal}
              sizes={isModal ? [1] : undefined}
            />
          </Box>
          {children ? (
            <Card p={3} flex={1} bgColor='#f9fafb'>
              {children}
            </Card>
          ) : null}
        </Flex>
      ) : (
        <DynamicFilters
          {...props}
          inputs={editFilters}
          show={filterActive || isModal}
        />
      )}
      {isMap ? (
        <Box zIndex={0} position='relative' flexGrow={1}>
          <MapCluster appointments={flatAppointments(filteredAppoint)} />
        </Box>
      ) : (
        <SimpleGrid
          flexGrow={1}
          overflow='auto'
          columns={nbCol}
          spacing={4}
          borderRadius='15px'
          borderTopLeftRadius={0}
          borderTopRightRadius={0}
        >
          {days.map((day) => (
            <CalendarColumn
              key={`index-${day.format('DD/MM/YYYY')}`}
              appointments={filteredAppoint[day.format('DD/MM/YYYY')]}
              onCheck={setSelectedDate}
              selected={selectedDate}
              day={day}
              forceDistance={forceDistance}
              formData={props.formData}
              setCurrDay={setCurrDay}
              isPC={isPC}
              // selectedUsers={selectedUsers.map(({ value }) => value)}
            />
          ))}
        </SimpleGrid>
      )}
    </Flex>
  );
};

export default Calendar;
